import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight, Instagram, Facebook, MessageCircle, Send, Globe, Building2, User, Mail, Phone } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const blogCategories = ['All Features', 'Vendor Events', 'Brand Partnerships', 'Styled Looks', 'Cultural Features', 'Behind the Brand', 'Community Highlights', 'New Collection Spotlights', 'Social Shop Features'];
const colabTypes = ['Vendor Event', 'Brand Partnership', 'Styled Shoot', 'Influencer Feature', 'Community Event', 'Media Feature', 'Social Shop Feature', 'Other'];
const blogPosts = [{
  id: 1,
  title: 'The Heritage Gala: A Night of Cultural Excellence',
  category: 'Vendor Events',
  date: 'June 15, 2024',
  excerpt: 'Exploring the vibrant intersection of traditional African textiles and modern high-fashion at the annual Heritage Gala.',
  image: 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?q=80&w=2070&auto=format&fit=crop'
}, {
  id: 2,
  title: 'Victoria Collection x Global Artisans',
  category: 'Brand Partnerships',
  date: 'May 28, 2024',
  excerpt: 'Go behind the scenes of our latest collaboration with master beadworkers from Nairobi, bringing authentic craftsmanship to our Victoria line.',
  image: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?q=80&w=1974&auto=format&fit=crop'
}, {
  id: 3,
  title: 'Summer in the City: Styled with Purpose',
  category: 'Styled Looks',
  date: 'May 12, 2024',
  excerpt: 'How to style our signature Kaftans for a modern urban summer. Discover the versatility of TriLyn pieces.',
  image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=2070&auto=format&fit=crop'
}, {
  id: 4,
  title: 'The Meaning Behind the Patterns',
  category: 'Cultural Features',
  date: 'April 20, 2024',
  excerpt: 'A deep dive into the historical significance of the Kente patterns used in our Zarrito collection.',
  image: 'https://images.unsplash.com/photo-1589156280159-27698a70f29e?q=80&w=1972&auto=format&fit=crop'
}];
// Custom TikTok icon
const TikTokIcon = ({
  size = 20
}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-535">
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" data-id="element-536" />
  </svg>;
export function Colabs() {
  useScreenInit();
  const [activeCategory, setActiveCategory] = useState('All Features');
  return <div className="min-h-screen bg-white" data-id="element-537">
      {/* Featured Post Section */}
      <section className="relative pt-10 pb-20 overflow-hidden" data-id="element-538">
        <div className="container mx-auto px-4" data-id="element-539">
          <div className="flex flex-col lg:flex-row gap-12 items-center" data-id="element-540">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} className="w-full lg:w-7/12 aspect-video overflow-hidden shadow-2xl" data-id="element-541">
              <img src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?q=80&w=1974&auto=format&fit=crop" alt="Featured Colab" className="w-full h-full object-cover" data-id="element-542" />
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} className="w-full lg:w-5/12 space-y-6" data-id="element-543">
              <div className="flex items-center gap-4 text-gold font-bold uppercase tracking-[0.3em] text-[10px]" data-id="element-544">
                <span data-id="element-545">Featured Story</span>
                <div className="w-12 h-[1px] bg-gold" data-id="element-546" />
              </div>
              <h1 className="text-4xl md:text-6xl font-serif leading-tight" data-id="element-547">
                Empowering Communities Through Design
              </h1>
              <div className="flex items-center gap-6 text-gray-400 text-xs uppercase tracking-widest" data-id="element-548">
                <div className="flex items-center gap-2" data-id="element-549">
                  <Tag size={14} className="text-gold" data-id="element-550" /> Brand Partnerships
                </div>
                <div className="flex items-center gap-2" data-id="element-551">
                  <Calendar size={14} className="text-gold" data-id="element-552" /> June 22, 2024
                </div>
              </div>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed" data-id="element-553">
                Discover how our latest partnership with local weaving
                cooperatives is creating sustainable growth while preserving
                ancient textile techniques.
              </p>
              <button className="group inline-flex items-center gap-4 text-black font-bold uppercase tracking-widest text-sm border-b-2 border-black pb-2 hover:text-gold hover:border-gold transition-all duration-300" data-id="element-554">
                Read Full Feature{' '}
                <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" data-id="element-555" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blog Hub Section */}
      <section className="py-20 bg-gray-50" data-id="element-556">
        <div className="container mx-auto px-4" data-id="element-557">
          <div className="text-center mb-16" data-id="element-558">
            <h2 className="text-3xl md:text-5xl font-serif mb-6 leading-tight" data-id="element-559">
              The TriLyn Hub
            </h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-10" data-id="element-560"></div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto" data-id="element-561">
              {blogCategories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-6 py-2.5 text-[10px] uppercase tracking-widest font-bold transition-all duration-300 border ${activeCategory === cat ? 'bg-black text-white border-black shadow-lg' : 'bg-white text-gray-400 border-gray-200 hover:border-gold hover:text-gold'}`} data-id="element-562">
                  {cat}
                </button>)}
            </div>
          </div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-id="element-563">
            {blogPosts.map((post, index) => <motion.div key={post.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="group bg-white flex flex-col h-full shadow-sm hover:shadow-xl transition-all duration-500" data-id="element-564">
                <div className="aspect-video overflow-hidden relative" data-id="element-565">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-566" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-black" data-id="element-567">
                    {post.category}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow" data-id="element-568">
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-3 block" data-id="element-569">
                    {post.date}
                  </span>
                  <h3 className="text-xl font-serif mb-4 group-hover:text-gold transition-colors leading-tight" data-id="element-570">
                    {post.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-grow" data-id="element-571">
                    {post.excerpt}
                  </p>
                  <button className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-black hover:text-gold transition-colors" data-id="element-572">
                    Read More <ArrowRight size={14} data-id="element-573" />
                  </button>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Social Feed Section */}
      <section className="py-24 bg-white" data-id="element-574">
        <div className="container mx-auto px-4" data-id="element-575">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6" data-id="element-576">
            <div className="space-y-4" data-id="element-577">
              <span className="text-gold uppercase tracking-[0.4em] text-xs font-bold block" data-id="element-578">
                Community
              </span>
              <h2 className="text-4xl font-serif" data-id="element-579">As Seen On Social</h2>
            </div>
            <div className="flex gap-4" data-id="element-580">
              <button className="p-3 border border-gray-200 rounded-full hover:bg-black hover:text-white transition-all duration-300" data-id="element-581">
                <Instagram size={20} data-id="element-582" />
              </button>
              <button className="p-3 border border-gray-200 rounded-full hover:bg-black hover:text-white transition-all duration-300" data-id="element-583">
                <TikTokIcon size={20} data-id="element-584" />
              </button>
              <button className="p-3 border border-gray-200 rounded-full hover:bg-black hover:text-white transition-all duration-300" data-id="element-585">
                <Facebook size={20} data-id="element-586" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-587">
            {[1, 2, 3, 4].map(i => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} viewport={{
            once: true
          }} className="aspect-square relative group overflow-hidden bg-gray-100" data-id="element-588">
                <img src={`https://images.unsplash.com/photo-${1500000000000 + i * 1000000}?q=80&w=1000&auto=format&fit=crop`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Social Preview" data-id="element-589" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-590">
                  <Instagram size={24} className="text-white" data-id="element-591" />
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Collaboration Request Section */}
      <section className="py-24 bg-black text-white relative overflow-hidden" data-id="element-592">
        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gold/5 -skew-x-12 translate-x-1/4 pointer-events-none" data-id="element-593" />

        <div className="container mx-auto px-4 relative z-10" data-id="element-594">
          <div className="max-w-5xl mx-auto" data-id="element-595">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20" data-id="element-596">
              <div className="space-y-8" data-id="element-597">
                <span className="text-gold uppercase tracking-[0.4em] text-xs font-bold block" data-id="element-598">
                  Partnerships
                </span>
                <h2 className="text-4xl md:text-5xl font-serif leading-tight" data-id="element-599">
                  Experience the Culture Through Style
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed" data-id="element-600">
                  We are always looking for visionary partners, creators, and
                  organizations to collaborate with. Whether it's a styled
                  shoot, a community event, or a brand partnership, we want to
                  hear from you.
                </p>
                <div className="space-y-6 pt-6" data-id="element-601">
                  <div className="flex items-center gap-4 text-sm" data-id="element-602">
                    <div className="p-3 bg-zinc-900 rounded-lg text-gold" data-id="element-603">
                      <MessageCircle size={20} data-id="element-604" />
                    </div>
                    <span data-id="element-605">Direct response within 48 hours</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm" data-id="element-606">
                    <div className="p-3 bg-zinc-900 rounded-lg text-gold" data-id="element-607">
                      <Globe size={20} data-id="element-608" />
                    </div>
                    <span data-id="element-609">Global collaboration opportunities</span>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 p-8 md:p-12 border border-zinc-800 shadow-2xl" data-id="element-610">
                <h3 className="text-2xl font-serif mb-8 text-gold" data-id="element-611">
                  Collaboration Request
                </h3>
                <form className="space-y-6" onSubmit={e => e.preventDefault()} data-id="element-612">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-613">
                    <div className="space-y-2" data-id="element-614">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-615">
                        Full Name
                      </label>
                      <div className="relative" data-id="element-616">
                        <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" data-id="element-617" />
                        <input type="text" className="w-full bg-black border border-zinc-800 rounded-none py-3 pl-12 pr-4 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-618" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-619">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-620">
                        Organization
                      </label>
                      <div className="relative" data-id="element-621">
                        <Building2 size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" data-id="element-622" />
                        <input type="text" className="w-full bg-black border border-zinc-800 rounded-none py-3 pl-12 pr-4 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-623" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-624">
                    <div className="space-y-2" data-id="element-625">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-626">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-627">
                        <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" data-id="element-628" />
                        <input type="email" className="w-full bg-black border border-zinc-800 rounded-none py-3 pl-12 pr-4 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-629" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-630">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-631">
                        Phone Number
                      </label>
                      <div className="relative" data-id="element-632">
                        <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" data-id="element-633" />
                        <input type="tel" className="w-full bg-black border border-zinc-800 rounded-none py-3 pl-12 pr-4 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-634" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-635">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-636">
                      Collaboration Type
                    </label>
                    <select className="w-full bg-black border border-zinc-800 rounded-none py-3 px-4 focus:outline-none focus:border-gold transition-colors text-sm appearance-none cursor-pointer" data-id="element-637">
                      {colabTypes.map(type => <option key={type} value={type} data-id="element-638">
                          {type}
                        </option>)}
                    </select>
                  </div>

                  <div className="space-y-2" data-id="element-639">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-640">
                      Message
                    </label>
                    <textarea rows={4} className="w-full bg-black border border-zinc-800 rounded-none py-3 px-4 focus:outline-none focus:border-gold transition-colors text-sm resize-none" placeholder="Tell us about your vision..." data-id="element-641"></textarea>
                  </div>

                  <button className="w-full bg-gold text-black py-4 text-xs uppercase tracking-widest font-bold hover:bg-white transition-all duration-300 flex items-center justify-center gap-3" data-id="element-642">
                    Submit Request <Send size={16} data-id="element-643" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}